import React from 'react';
import { Box, Card, Typography } from '@mui/material';
import VisuallyHidden from '../../../components/VisuallyHidden';
import { PipelineCounts } from '../onboardingIntelligence.types';
import {
    CARD_SX,
    HEADING_NAVY,
    PipelineStatusKey,
    SECTION_LABEL_SX,
    STATUS_PALETTE,
} from './onboardingIntelligence.styles';

interface Segment {
    key: PipelineStatusKey;
    label: string;
    barFill: string;
    dotColor: string;
    count: number;
}

const buildSegments = (pipeline: PipelineCounts): Segment[] =>
    (Object.keys(STATUS_PALETTE) as PipelineStatusKey[]).map((key) => ({
        key,
        label: STATUS_PALETTE[key].label,
        barFill: STATUS_PALETTE[key].barFill,
        dotColor: STATUS_PALETTE[key].solid,
        count: pipeline[key],
    }));

interface Props {
    pipeline: PipelineCounts;
}

const PipelineBreakdownBar: React.FC<Props> = ({ pipeline }) => {
    const segments = buildSegments(pipeline);
    const total = segments.reduce((acc, s) => acc + s.count, 0);

    return (
        <Card
            data-testid='onboarding-intelligence-pipeline'
            sx={{
                ...CARD_SX,
                width: '100%',
                padding: '16px 20px',
            }}
        >
            <Typography
                variant='subtitle2'
                sx={{ ...SECTION_LABEL_SX, marginBottom: '12px' }}
            >
                Pipeline Breakdown
            </Typography>
            {/*
             * The bar is decorative: the legend below carries the semantic
             * data (each colored dot is paired with a text label), and the
             * VisuallyHidden <dl> provides the same data to screen readers.
             * `aria-hidden` exempts pastel fills from WCAG 1.4.11.
             */}
            <Box
                aria-hidden
                sx={{
                    display: 'flex',
                    width: '100%',
                    height: '12px',
                    borderRadius: '999px',
                    overflow: 'hidden',
                    backgroundColor: '#eceff1',
                }}
            >
                {total === 0 ? (
                    <Box
                        sx={{
                            flex: 1,
                            backgroundColor: '#eceff1',
                            color: 'rgba(0,0,0,0.5)',
                            textAlign: 'center',
                            fontSize: '11px',
                            lineHeight: '12px',
                        }}
                    >
                        No suppliers in pipeline
                    </Box>
                ) : (
                    segments
                        .filter((s) => s.count > 0)
                        .map((s) => (
                            <Box
                                key={s.key}
                                data-testid={`onboarding-intelligence-pipeline-segment-${s.key}`}
                                sx={{ flex: s.count, backgroundColor: s.barFill }}
                            />
                        ))
                )}
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    marginTop: '12px',
                    gap: '16px',
                }}
            >
                {segments.map((s) => (
                    <Box
                        key={s.key}
                        sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                        data-testid={`onboarding-intelligence-pipeline-legend-${s.key}`}
                    >
                        <Box
                            aria-hidden
                            sx={{
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                backgroundColor: s.dotColor,
                                opacity: s.count === 0 ? 0.4 : 1,
                            }}
                        />
                        <Typography
                            variant='caption'
                            sx={{
                                color: s.count === 0 ? 'rgba(0,0,0,0.5)' : HEADING_NAVY,
                            }}
                        >
                            {s.label} ({s.count})
                        </Typography>
                    </Box>
                ))}
            </Box>
            <VisuallyHidden>
                <dl>
                    {segments.map((s) => (
                        <React.Fragment key={s.key}>
                            <dt>{s.label}</dt>
                            <dd>{s.count}</dd>
                        </React.Fragment>
                    ))}
                </dl>
            </VisuallyHidden>
        </Card>
    );
};

export default PipelineBreakdownBar;
